import { useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  List,
  ListItem,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Modal } from '@/design-system/components/Modal';

const ADD_NEW_SENTINEL = '__add_new__';

const importFormSchema = z.object({
  dataset: z.string().min(1, 'Category is required'),
});

type ImportFormData = z.infer<typeof importFormSchema>;

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultDataset?: string;
  onImport: (dataset: string, file: File) => void;
  isLoading: boolean;
  result?: { imported: number; updated?: number; errors: string[] } | null;
  existingDatasets?: string[];
}


export function ImportModal({ isOpen, onClose, defaultDataset, onImport, isLoading, result, existingDatasets = [] }: ImportModalProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [isAddingNewDataset, setIsAddingNewDataset] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<ImportFormData>({
    resolver: zodResolver(importFormSchema),
    defaultValues: { dataset: defaultDataset ?? '' },
  });

  const handleFile = (data: ImportFormData) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    onImport(data.dataset, file);
  };

  const showDatasetInput = isAddingNewDataset || existingDatasets.length === 0;

  const resultImported = result ? ((result.imported ?? 0) + (result.updated ?? 0)) : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Data"
      size="md"
      footer={
        result ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <HStack spacing={3} w="full" justify="flex-end">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" isLoading={isLoading} onClick={handleSubmit(handleFile)}>
              Import
            </Button>
          </HStack>
        )
      }
    >
      <VStack spacing={4} align="stretch">
        {result ? (
          <Alert status={(result.errors ?? []).length > 0 ? 'warning' : 'success'} borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>
                {result.imported ? `${result.imported} row(s) created` : ''}
                {result.imported && result.updated ? ', ' : ''}
                {result.updated ? `${result.updated} row(s) updated` : ''}
                {!result.imported && !result.updated ? `${resultImported} row(s) imported` : ''}
              </AlertTitle>
              {(result.errors ?? []).length > 0 && (
                <AlertDescription>
                  <Text fontSize="sm" mt={1}>
                    {result.errors.length} error(s):
                  </Text>
                  <List spacing={1} mt={1}>
                    {result.errors.map((e, i) => (
                      <ListItem key={i}>
                        <Text fontSize="xs" color="orange.600">{e}</Text>
                      </ListItem>
                    ))}
                  </List>
                </AlertDescription>
              )}
            </Box>
          </Alert>
        ) : (
          <>
            <FormControl isInvalid={!!errors.dataset} isRequired>
              <FormLabel color="text-primary" mb={1}>
                Category
              </FormLabel>
              <Text fontSize="sm" color="text-secondary" mb={2}>
                Group name for the items in this file (e.g. <em>orders</em>, <em>machines</em>)
              </Text>

              {showDatasetInput ? (
                <HStack spacing={2}>
                  <Input
                    {...register('dataset')}
                    placeholder="e.g. orders"
                    bg="bg-surface"
                    borderColor="border-default"
                    _hover={{ borderColor: 'border-hover' }}
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                  {existingDatasets.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setIsAddingNewDataset(false);
                        setValue('dataset', existingDatasets[0]);
                      }}
                      flexShrink={0}
                    >
                      ← Pick existing
                    </Button>
                  )}
                </HStack>
              ) : (
                <Select
                  {...register('dataset', {
                    onChange: (e) => {
                      if (e.target.value === ADD_NEW_SENTINEL) {
                        setIsAddingNewDataset(true);
                        setValue('dataset', '');
                      }
                    },
                  })}
                  bg="bg-surface"
                  borderColor="border-default"
                  _hover={{ borderColor: 'border-hover' }}
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                >
                  <option value="">Select a category…</option>
                  {existingDatasets.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                  <option value={ADD_NEW_SENTINEL}>＋ Add new category</option>
                </Select>
              )}
              <FormErrorMessage>{errors.dataset?.message}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel color="text-primary" fontSize="sm">
                File
              </FormLabel>
              <Input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv"
                size="sm"
                bg="bg-surface"
                borderColor="border-default"
                p={1}
              />
              <Text fontSize="xs" color="text-secondary" mt={2} lineHeight="tall">
                Supports <strong>.csv</strong> and <strong>.xlsx</strong>. Required column:{' '}
                <strong>name</strong> (or <strong>label</strong>). Optional: <strong>key</strong>{' '}
                (updates existing row by code), <strong>belongs_to</strong> (parent item code),{' '}
                <strong>latitude</strong>/<strong>longitude</strong> (when the category has location
                enabled). Any other columns become additional info attributes.
              </Text>
            </FormControl>
          </>
        )}
      </VStack>
    </Modal>
  );
}
